import React, { useState } from 'react';
import {
  ShieldCheck,
  Download,
  Activity,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Database,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/sonner';
import { supabase } from '@/lib/supabase';

/**
 * Admin-only Data Protection panel.
 *
 * Provides a pre-migration, read-only safeguard surface:
 *   1. Full Data Backup — JSON export of profiles, family_graph_members,
 *      family_graph_memories, media, invites, households + a manifest of
 *      every object in the family-media storage bucket scoped to the user.
 *   2. Integrity Check — verifies media/memory/member counts and confirms
 *      the storage bucket and profile linkage are intact.
 *
 * Strict scope: NO mutations. Never deletes, resets, or modifies any
 * record. Safe to run before any migration or backend change.
 */
interface DataProtectionPanelProps {
  userId: string | null;
  isAdmin: boolean;
}

interface IntegrityReport {
  status: string;
  media_count: number;
  memory_count: number;
  member_count: number;
  storage_file_count: number;
  profile_linked: boolean;
  checked_at: string;
}

const DataProtectionPanel: React.FC<DataProtectionPanelProps> = ({
  userId,
  isAdmin,
}) => {
  const [downloading, setDownloading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [report, setReport] = useState<IntegrityReport | null>(null);

  const handleDownload = async (mode: 'self' | 'admin-all') => {
    if (!userId) {
      toast.error('You must be signed in to download a backup.');
      return;
    }
    setDownloading(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        'data-backup-export',
        { body: { mode } }
      );
      if (error) {
        toast.error(error.message || 'Backup failed');
        return;
      }
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const stamp = new Date().toISOString().split('T')[0];
      a.href = url;
      a.download = `kinsvault-data-backup-${mode}-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Backup downloaded');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Backup failed';
      toast.error(msg);
    } finally {
      setDownloading(false);
    }
  };

  const handleCheck = async () => {
    if (!userId) return;
    setChecking(true);
    setReport(null);
    try {
      const { data, error } = await supabase.functions.invoke(
        'data-integrity-check',
        { body: {} }
      );
      if (error) {
        toast.error(error.message || 'Integrity check failed');
        return;
      }
      setReport(data as IntegrityReport);
      toast.success('Integrity check complete');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Check failed';
      toast.error(msg);
    } finally {
      setChecking(false);
    }
  };

  return (
    <Card className="border-indigo-200 bg-gradient-to-br from-indigo-50/40 to-blue-50/40">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Database className="w-5 h-5 text-indigo-600" />
          Data Protection
          <span className="ml-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-100 border border-indigo-200 text-indigo-700 text-[10px] font-semibold uppercase tracking-wide">
            <ShieldCheck className="w-3 h-3" />
            Pre-launch
          </span>
        </CardTitle>
        <CardDescription>
          Pre-migration safeguards. Read-only — never deletes or alters any
          family data, photos, or videos.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="rounded-lg border border-slate-200 bg-white/70 p-3 text-sm text-slate-700 space-y-1.5">
          <div className="flex items-center gap-2 font-semibold text-slate-900">
            <ShieldCheck className="w-4 h-4 text-emerald-600" />
            Protected resources
          </div>
          <ul className="list-disc list-inside text-xs text-slate-600 space-y-0.5 ml-1">
            <li>
              Storage bucket{' '}
              <code className="font-mono bg-slate-100 px-1 rounded">
                family-media
              </code>{' '}
              is persistent and never recreated by deploys.
            </li>
            <li>
              Tables (profiles, family_graph_members, family_graph_memories,
              media, invites, households) use soft-delete columns — rows
              are never hard-deleted by migrations.
            </li>
            <li>
              Media records stay linked to user_id and storage path even
              after auth or backend changes.
            </li>
            <li>Every backup and integrity check is logged for audit.</li>
          </ul>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            onClick={() => handleDownload('self')}
            disabled={downloading || !userId}
            className="rounded-full bg-indigo-600 hover:bg-indigo-700 text-white gap-1.5"
          >
            {downloading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            Download my data backup
          </Button>

          {isAdmin && (
            <Button
              variant="outline"
              onClick={() => handleDownload('admin-all')}
              disabled={downloading || !userId}
              className="rounded-full gap-1.5 border-indigo-300 text-indigo-700 hover:bg-indigo-50"
            >
              {downloading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Database className="w-4 h-4" />
              )}
              Admin: full snapshot
            </Button>
          )}

          <Button
            variant="outline"
            onClick={handleCheck}
            disabled={checking || !userId}
            className="rounded-full gap-1.5"
          >
            {checking ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Activity className="w-4 h-4" />
            )}
            Run integrity check
          </Button>
        </div>

        {report && (
          <div className="rounded-lg border border-slate-200 bg-white p-3 text-sm">
            <div className="flex items-center gap-1.5 font-semibold text-slate-900 mb-2">
              {report.status === 'healthy' ? (
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              ) : (
                <AlertTriangle className="w-4 h-4 text-amber-600" />
              )}
              Integrity report
              <span className="ml-auto text-[10px] text-slate-400 font-normal">
                {new Date(report.checked_at).toLocaleString()}
              </span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
              <Stat label="Profile linked" value={report.profile_linked ? 'Yes' : 'No'} />
              <Stat label="Family members" value={report.member_count} />
              <Stat label="Memories" value={report.memory_count} />
              <Stat label="Media records" value={report.media_count} />
              <Stat label="Storage files" value={report.storage_file_count} />
              <Stat label="Status" value={report.status} />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const Stat: React.FC<{ label: string; value: string | number }> = ({
  label,
  value,
}) => (
  <div className="rounded-md border border-slate-200 bg-slate-50 px-2 py-1.5">
    <div className="text-[10px] uppercase tracking-wide text-slate-500">
      {label}
    </div>
    <div className="font-semibold text-slate-800">{String(value)}</div>
  </div>
);

export default DataProtectionPanel;
